import {afterNextRender, Component, ElementRef, NgZone, OnDestroy, OnInit, viewChild} from '@angular/core'
import {PUBLIC_ROUTES} from '../../../../shared/routing/public.routes'
import {AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms'
import {ActivatedRoute, Router} from '@angular/router'
import {FormService} from '../../../../core/forms/form.service'
import {
  ExpectedExecution,
  JobRequestDetail_CalculatePriceRangeGql,
  OfferingType,
  Ownership,
} from '../../../../../__generated__/types'
import {Observable, Subject} from 'rxjs'
import {filter, map, startWith, takeUntil} from 'rxjs/operators'
import {JobRequestRepository} from '../job-request.repository'
import {ClientLastJob, JobRequestService} from '../job-request.service'
import {FooterProgressSteps} from '../job-request-footer/job-request-footer.component'
import {PriceRange} from '../../../../craft/price-estimation/price-range.types'
import {faInfoCircle} from '@fortawesome/pro-regular-svg-icons/faInfoCircle'
import {OfferingResolved} from '../resolvers/job-request-offering.resolver'
import {AgencyResolved} from '../resolvers/job-request-agency.resolver'
import {conditionalValidator} from '../../../../craft/forms/form.types'
import {PageComponent, PageMetadata} from '../../../../core/seo/page-metadata'
import {JobRequestPageMetadataService} from '../job-request-page-metadata.service'
import {AddressComponents, GoogleMapsService} from '../../../../core/geo/google-maps.service'

@Component({
  templateUrl: './job-request-execution-details-page.component.html',
  host: {ngSkipHydration: 'true'},
  standalone: false,
})
export class JobRequestExecutionDetailsPageComponent extends PageComponent implements OnInit, OnDestroy {
  readonly offering: OfferingResolved = this.route.snapshot.data['offering'] ?? {
    label: this.repository.readJobRequestDetailState().title,
    executionAddressEnabled: true,
  }
  readonly executionDates: ExpectedExecution[]
  readonly clientLastJob: ClientLastJob | undefined = this.route.snapshot.data['clientLastJob']
  formConfiguration?: {skipExecutionDate: boolean; isOwnershipQuestionOptional: boolean}
  priceRange$?: Observable<PriceRange | undefined>

  private readonly intentionSlug = this.route.snapshot.paramMap.get('intentionSlug')
  readonly previousPath = PUBLIC_ROUTES.JOB_REQUEST_DETAIL
  private readonly nextPath = PUBLIC_ROUTES.JOB_REQUEST_CONTACT
  private readonly unsubscribe$ = new Subject<void>()

  readonly ExpectedExecution = ExpectedExecution
  readonly infoIcon = faInfoCircle
  readonly Ownership = Ownership
  readonly agency?: AgencyResolved = this.route.snapshot.data['agency']

  readonly executionAddressControls = {
    streetAndHouseNumber: new UntypedFormControl('', [Validators.required]),
    postalCode: new UntypedFormControl('', [
      Validators.required,
      Validators.maxLength(4),
      Validators.minLength(4),
      FormService.wholeNumberValidator(),
    ]),
    locality: new UntypedFormControl('', [Validators.required]),
  }

  controls = {
    expectedExecution: new UntypedFormControl(undefined, [Validators.required]),
    ownership: this.offering?.executionAddressEnabled
      ? new UntypedFormControl(
          undefined,
          conditionalValidator(
            () => !this.formConfiguration?.isOwnershipQuestionOptional && !!this.offering?.executionAddressEnabled,
            [Validators.required],
          ),
        )
      : undefined,
    floor: this.offering?.executionAddressEnabled ? new UntypedFormControl('', []) : undefined,
    executionAddress: this.offering?.executionAddressEnabled
      ? new UntypedFormGroup(this.executionAddressControls)
      : undefined,
  }

  readonly form = this.fb.group(this.controls)

  private readonly controlsForProgressCalculation: AbstractControl[] = [
    this.controls.expectedExecution,
    this.executionAddressControls.streetAndHouseNumber,
    this.form,
  ]
  readonly progressSteps: FooterProgressSteps = {
    currentStep$: this.form.valueChanges.pipe(
      startWith(this.form),
      map(() => {
        return this.controlsForProgressCalculation.filter(c => c.valid).length + 1
      }),
    ),
    //start off with 1 valid step already done i.e. the job that was filled out on previous page
    numberOfSteps: this.controlsForProgressCalculation.length + 1,
  }

  addressInputRef = viewChild<ElementRef>('address')

  protected readonly OfferingType = OfferingType

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly ngZone: NgZone,
    private readonly calculatePriceRangeGql: JobRequestDetail_CalculatePriceRangeGql,
    private readonly jobRequestPageMetadataService: JobRequestPageMetadataService,
    private readonly googleMapsService: GoogleMapsService,
    readonly jobRequestService: JobRequestService,
    readonly repository: JobRequestRepository,
  ) {
    super()
    this.executionDates = this.offering?.executionDates?.length
      ? this.offering.executionDates
      : [
          ExpectedExecution.Asap,
          ExpectedExecution.Next_30Days,
          ExpectedExecution.Next_3Months,
          ExpectedExecution.MoreThan_3Months,
        ]

    afterNextRender({read: () => this.setupAutocomplete()})
  }

  getPageMetadata(): Observable<PageMetadata> {
    return this.jobRequestPageMetadataService.getPageMetadataForStep('EXECUTION', this.offering)
  }

  ngOnInit(): void {
    this.formConfiguration = this.hydrateForm()
    this.calculatePriceRange()
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next()
    this.unsubscribe$.complete()
  }

  private hydrateForm() {
    const damageState = this.repository.readJobRequestDamageState()
    const executionState = this.repository.readJobRequestExecutionState()
    if (executionState) {
      this.form.patchValue(executionState)
    } else if (this.clientLastJob) {
      // if there's nothing in state, auto-fill with the last job of the client
      this.controls.ownership?.patchValue(this.clientLastJob.executionAddress?.ownership)
      this.controls.executionAddress?.patchValue(
        this.clientLastJob.executionAddress ?? this.clientLastJob.billingAddress ?? {},
      )
    }
    return {
      skipExecutionDate: this.jobRequestService.canSkipExecutionDateQuestion({execution: executionState}),
      isOwnershipQuestionOptional: this.jobRequestService.isOwnershipQuestionOptional({damage: damageState}),
    }
  }

  private calculatePriceRange() {
    const offering = this.repository.readJobRequestOfferingState()
    if (offering?.staticQuestions && Object.values(offering?.staticQuestions).some(value => !!value)) {
      this.priceRange$ = this.calculatePriceRangeGql
        .fetch({
          jobQuestions: offering?.staticQuestions,
        })
        .pipe(map(value => value.data.calculatePriceRange))
    }
  }

  next(): void {
    this.form.markAllAsTouched()
    if (this.form.valid) {
      this.repository.storeJobRequestExecutionState({
        ...this.form.getRawValue(),
        skipExecutionDate: this.formConfiguration?.skipExecutionDate,
      })
      this.router.navigate([this.nextPath.routingPath.replace(':intentionSlug', this.intentionSlug ?? '')], {
        relativeTo: this.route.parent,
        queryParamsHandling: 'merge',
      })
    }
  }

  previous(): void {
    if (this.form.valid) {
      this.repository.storeJobRequestExecutionState({
        ...this.form.getRawValue(),
        skipExecutionDate: this.formConfiguration?.skipExecutionDate,
      })
    }
    this.router.navigate([this.previousPath.routingPath], {
      relativeTo: this.route.parent,
      queryParamsHandling: 'merge',
    })
  }

  private setupAutocomplete() {
    this.googleMapsService
      .googleMapsApi$()
      .pipe(
        takeUntil(this.unsubscribe$),
        // if it fails to load, we won't have autocomplete, no need for further error handling
        filter(({isLoaded}) => isLoaded),
      )
      .subscribe(maps => {
        if (!maps.isLoaded) {
          return
        }

        const addressInput = this.addressInputRef()
        if (addressInput) {
          this.addAutocompleteToElement(addressInput)
        }
      })
  }

  private addAutocompleteToElement(element: ElementRef<HTMLInputElement>) {
    // create the autocomplete object outside the zone so its events don't trigger change detection.
    // we'll bring it back in inside the autocomplete listener
    this.ngZone.runOutsideAngular(() => {
      const autocomplete = new google.maps.places.Autocomplete(element.nativeElement, {
        fields: ['address_components'],
        types: ['address'], // only return exact addresses
        componentRestrictions: {country: ['ch']},
      })
      autocomplete.addListener('place_changed', () => {
        const components = autocomplete.getPlace().address_components
        if (!components) return

        this.ngZone.run(() => {
          const address = this.googleMapsService.parseAddressComponents(components)
          this.setAutocompletedAddress(address)
        })
      })
    })
  }

  private setAutocompletedAddress(address: AddressComponents) {
    if (address.locality) {
      this.executionAddressControls.locality.setValue(address.locality)
    }
    if (address.postalCode) {
      this.executionAddressControls.postalCode.setValue(address.postalCode)
    }
    if (address.streetAndHouseNumber) {
      this.executionAddressControls.streetAndHouseNumber.setValue(address.streetAndHouseNumber)
    }
  }
}
